import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Badge } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { computeAdoptionStats } from "app/pages/sales-intelligence/modules/extension-management/services/computeAdoptionStats";
import type { ExtensionGovernanceUser } from "app/pages/sales-intelligence/modules/extension-management/types";

import { SettingsSectionBlock } from "../SettingsSectionBlock/SettingsSectionBlock";
import { SettingsSectionTitle } from "../SettingsSectionTitle/SettingsSectionTitle";

import { AdminManagedContent } from "./AdminManagedContent";
import { SelfInstallContent } from "./SelfInstallContent";

const ADMIN_MANAGED_THRESHOLD = 0.9;

type DeploymentMethodSectionProps = {
    users: ExtensionGovernanceUser[];
};

export const DeploymentMethodSection = ({ users }: DeploymentMethodSectionProps) => {
    const { translate } = useTranslate();

    const isAdminManaged = useMemo(() => {
        if (users.length === 0) {
            return false;
        }
        const { installs } = computeAdoptionStats(users);
        return installs / users.length >= ADMIN_MANAGED_THRESHOLD;
    }, [users]);

    const notInstalledEmails = useMemo(
        () => users.filter((u) => u.status === "NotInstalled").map((u) => u.userEmail),
        [users],
    );

    return (
        <SettingsSectionBlock dataAutomation="extension-deployment-method-section-title">
            <Flex
                alignItems="center"
                justifyContent="space-between"
            >
                <SettingsSectionTitle
                    title={translate("si.management.extension.deployment.title")}
                    description={translate("si.management.extension.deployment.subtitle")}
                    dataAutomation="extension-deployment-method-section-title"
                />
                <Badge
                    type="custom"
                    bgColor={isAdminManaged ? colorsPalettes.mint["100"] : colorsPalettes.orange["100"]}
                    color={isAdminManaged ? colorsPalettes.mint["500"] : colorsPalettes.orange["400"]}
                >
                    {translate(
                        isAdminManaged
                            ? "si.management.extension.deployment.badge.admin_managed"
                            : "si.management.extension.deployment.badge.self_install",
                    )}
                </Badge>
            </Flex>
            {isAdminManaged ? <AdminManagedContent /> : <SelfInstallContent notInstalledEmails={notInstalledEmails} />}
        </SettingsSectionBlock>
    );
};
